<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>积分订单详情</span>
        <el-button @click="backToList">返回列表</el-button>
      </div>

      <el-descriptions :column="2" border v-loading="loading">
        <el-descriptions-item label="订单号">{{ detail.orderNo || '-' }}</el-descriptions-item>
        <el-descriptions-item label="订单状态">{{ statusText(detail.orderStatus) }}</el-descriptions-item>
        <el-descriptions-item label="消耗积分">{{ detail.pointsAmount || 0 }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDateTime(detail.createTime) }}</el-descriptions-item>
        <el-descriptions-item label="发货时间">{{ formatDateTime(detail.deliveryTime) }}</el-descriptions-item>
        <el-descriptions-item label="收货时间">{{ formatDateTime(detail.receiveTime) }}</el-descriptions-item>
        <el-descriptions-item label="收货人">{{ detail.receiverName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="手机号">{{ detail.receiverMobile || '-' }}</el-descriptions-item>
        <el-descriptions-item label="收货地址" :span="2">{{ fullAddress }}</el-descriptions-item>
        <el-descriptions-item v-if="detail.expressCompany" label="物流信息" :span="2">
          {{ detail.expressCompany }} {{ detail.expressNo }}
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <el-card shadow="never" style="margin-top: 20px">
      <div slot="header">商品明细</div>
      <el-table :data="detail.items || []" border v-loading="loading">
        <el-table-column prop="productName" label="商品名称" />
        <el-table-column prop="pointsCost" label="单件积分" width="120" />
        <el-table-column prop="quantity" label="数量" width="100" />
        <el-table-column prop="totalPoints" label="总积分" width="120" />
      </el-table>
    </el-card>

    <el-card v-if="Number(detail.orderStatus) === 0" shadow="never" style="margin-top: 20px">
      <div slot="header">订单发货</div>
      <el-form label-width="100px">
        <el-form-item label="快递公司">
          <el-select
            v-model="delivery.expressCompany"
            filterable
            clearable
            :filter-method="filterDeliveryCompanies"
            :loading="deliveryCompanyLoading"
            placeholder="请选择发货快递"
            style="width: 100%"
          >
            <el-option
              v-for="item in visibleDeliveryCompanyOptions"
              :key="item.code || item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
            <div class="delivery-company-actions">
              <span class="delivery-company-actions__hint">
                {{ deliveryCompanyKeyword ? `搜索结果 ${visibleDeliveryCompanyOptions.length} 项` : `默认展示常用快递 ${visibleDeliveryCompanyOptions.length} 项` }}
              </span>
              <el-button
                v-if="!deliveryCompanyKeyword"
                type="text"
                @click="toggleAllDeliveryCompanies"
              >
                {{ showAllDeliveryCompanies ? '收起到常用快递' : '显示全部物流公司' }}
              </el-button>
          </div>
        </el-form-item>
        <el-form-item label="物流单号">
          <el-input v-model.trim="delivery.expressNo" />
        </el-form-item>
      </el-form>
      <el-button type="primary" :loading="submitting" @click="submitDelivery">确认发货</el-button>
    </el-card>
  </div>
</template>

<script>
import { deliveryPointOrder, getPointOrderDetail, getWechatDeliveryCompanies } from '../../api/admin'
import { formatDateTime } from '../../utils/format'

export default {
  props: {
    orderNo: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      deliveryCompanyLoading: false,
      detail: { items: [] },
      deliveryCompanyOptions: [],
      deliveryCompanyFullLoaded: false,
      deliveryCompanyKeyword: '',
      showAllDeliveryCompanies: false,
      delivery: {
        expressCompany: '',
        expressNo: ''
      }
    }
  },
  computed: {
    fullAddress() {
      return [this.detail.receiverProvince, this.detail.receiverCity, this.detail.receiverDistrict, this.detail.receiverAddress]
        .filter(Boolean)
        .join('')
    },
    visibleDeliveryCompanyOptions() {
      const keyword = (this.deliveryCompanyKeyword || '').trim().toLowerCase()
      if (keyword) {
        return this.deliveryCompanyOptions.filter(item =>
          (item.label || '').toLowerCase().includes(keyword) ||
          (item.name || '').toLowerCase().includes(keyword) ||
          (item.code || '').toLowerCase().includes(keyword)
        )
      }
      if (this.showAllDeliveryCompanies) {
        return this.deliveryCompanyOptions
      }
      return this.deliveryCompanyOptions
    }
  },
  created() {
    this.loadDetail()
    this.loadDeliveryCompanies(false)
  },
  methods: {
    formatDateTime,
    statusText(status) {
      const map = { 0: '待发货', 1: '待收货', 2: '已完成', 3: '已取消' }
      return map[status] || '未知状态'
    },
    async loadDetail() {
      this.loading = true
      try {
        this.detail = await getPointOrderDetail(this.orderNo)
      } finally {
        this.loading = false
      }
    },
    async loadDeliveryCompanies(full = false) {
      this.deliveryCompanyLoading = true
      try {
        const list = await getWechatDeliveryCompanies({ full })
        this.deliveryCompanyOptions = (list || []).map(item => ({
          code: item.code,
          name: item.name,
          value: item.name || item.code,
          label: item.name && item.code ? `${item.name}（${item.code}）` : (item.name || item.code || '')
        })).filter(item => item.value)
        this.deliveryCompanyFullLoaded = full
        this.deliveryCompanyKeyword = ''
        this.showAllDeliveryCompanies = false
      } finally {
        this.deliveryCompanyLoading = false
      }
    },
    async ensureFullDeliveryCompaniesLoaded() {
      if (this.deliveryCompanyFullLoaded || this.deliveryCompanyLoading) {
        return
      }
      await this.loadDeliveryCompanies(true)
    },
    filterDeliveryCompanies(keyword) {
      this.deliveryCompanyKeyword = keyword || ''
      if (this.deliveryCompanyKeyword) {
        this.showAllDeliveryCompanies = true
        this.ensureFullDeliveryCompaniesLoaded()
      }
    },
    async toggleAllDeliveryCompanies() {
      if (!this.showAllDeliveryCompanies) {
        await this.ensureFullDeliveryCompaniesLoaded()
        this.showAllDeliveryCompanies = true
        return
      }
      this.showAllDeliveryCompanies = false
    },
    async submitDelivery() {
      if (!this.delivery.expressCompany || !this.delivery.expressNo) {
        this.$message.error('请填写快递公司和物流单号')
        return
      }
      this.submitting = true
      try {
        await deliveryPointOrder({
          orderNo: this.orderNo,
          expressCompany: this.delivery.expressCompany,
          expressNo: this.delivery.expressNo
        })
        this.$message.success('发货成功')
        this.backToList()
      } finally {
        this.submitting = false
      }
    },
    backToList() {
      this.$router.push({
        path: '/point-mall/orders',
        query: this.$route.query.q ? { q: this.$route.query.q } : {}
      })
    }
  }
}
</script>

<style scoped>
.card-header { display: flex; justify-content: space-between; align-items: center; }

.delivery-company-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 24px;
}

.delivery-company-actions__hint {
  color: #8b7a68;
  font-size: 12px;
}
</style>
